import { useState } from "react";
import {
  useConnectSupplier,
  useConnectedSuppliers,
  useCreateRestaurant,
  useCreateSupplier,
  useDisconnectSupplier,
  useRestaurants,
  useSuppliers,
} from "../../api/admin.js";
import { AdminShell } from "../../components/AdminShell.js";
import { DataTable, type Column } from "../../components/DataTable.js";
import { Field, buttonClass, inputClass } from "../../components/Field.js";

type Org = { id: string; name: string; isActive: boolean };

function RestaurantSupplierPanel({
  restaurant,
  suppliers,
}: {
  restaurant: Org;
  suppliers: { id: string; name: string }[];
}) {
  const connected = useConnectedSuppliers(restaurant.id);
  const connect = useConnectSupplier();
  const disconnect = useDisconnectSupplier();
  const connectedIds = new Set(connected.data ?? []);

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50 p-4">
      <h3 className="mb-2 font-semibold">{restaurant.name} · 연결된 공급업체</h3>
      <ul className="space-y-1">
        {suppliers.map((s) => (
          <li key={s.id}>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={connectedIds.has(s.id)}
                disabled={connect.isPending || disconnect.isPending}
                onChange={(e) => {
                  const vars = { restaurantId: restaurant.id, supplierId: s.id };
                  if (e.target.checked) connect.mutate(vars);
                  else disconnect.mutate(vars);
                }}
              />
              {s.name}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OrgForm({
  idPrefix,
  create,
}: {
  idPrefix: string;
  create: { mutate: (body: unknown) => void; isPending: boolean; error: unknown };
}) {
  const [form, setForm] = useState({ name: "", contactName: "", phone: "" });

  return (
    <form
      className="mb-4 grid gap-4 rounded-xl border border-stone-200 bg-white p-4 md:grid-cols-3"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate({ name: form.name, contactName: form.contactName || null, phone: form.phone || null });
        setForm({ name: "", contactName: "", phone: "" });
      }}
    >
      <Field label="이름" htmlFor={`${idPrefix}-name`}>
        <input
          id={`${idPrefix}-name`}
          required
          className={inputClass}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </Field>
      <Field label="담당자" htmlFor={`${idPrefix}-contact`}>
        <input
          id={`${idPrefix}-contact`}
          className={inputClass}
          value={form.contactName}
          onChange={(e) => setForm({ ...form, contactName: e.target.value })}
        />
      </Field>
      <Field label="연락처" htmlFor={`${idPrefix}-phone`}>
        <input
          id={`${idPrefix}-phone`}
          inputMode="tel"
          className={inputClass}
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </Field>
      {create.error ? (
        <p role="alert" className="text-sm text-red-600 md:col-span-3">
          {(create.error as Error).message}
        </p>
      ) : null}
      <button type="submit" className={`${buttonClass} md:col-span-3`} disabled={create.isPending}>
        추가
      </button>
    </form>
  );
}

export function AdminOrgsPage() {
  const restaurants = useRestaurants();
  const suppliers = useSuppliers();
  const createSupplier = useCreateSupplier();
  const createRestaurant = useCreateRestaurant();
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null);

  const supplierColumns: Column<Org>[] = [
    { key: "name", label: "이름", sortValue: (o) => o.name, render: (o) => o.name },
  ];

  const restaurantColumns: Column<Org>[] = [
    { key: "name", label: "이름", sortValue: (o) => o.name, render: (o) => o.name },
    {
      key: "actions",
      label: "",
      render: (o) => (
        <button
          type="button"
          className="text-sm text-brand-700 underline"
          onClick={() => setSelectedRestaurantId(o.id === selectedRestaurantId ? null : o.id)}
        >
          연결 관리
        </button>
      ),
    },
  ];

  const selectedRestaurant = restaurants.data?.find((r) => r.id === selectedRestaurantId);

  return (
    <AdminShell title="조직 관리">
      <section className="mb-8">
        <h2 className="mb-2 font-semibold">공급업체</h2>
        <OrgForm idPrefix="supplier" create={createSupplier} />
        <DataTable
          columns={supplierColumns}
          rows={suppliers.data ?? []}
          rowKey={(o) => o.id}
          emptyMessage="등록된 공급업체가 없습니다."
          isPending={suppliers.isPending}
        />
      </section>

      <section>
        <h2 className="mb-2 font-semibold">식당</h2>
        <OrgForm idPrefix="restaurant" create={createRestaurant} />
        <DataTable
          columns={restaurantColumns}
          rows={restaurants.data ?? []}
          rowKey={(o) => o.id}
          emptyMessage="등록된 식당이 없습니다."
          isPending={restaurants.isPending}
        />
        {selectedRestaurant ? (
          <div className="mt-4">
            <RestaurantSupplierPanel restaurant={selectedRestaurant} suppliers={suppliers.data ?? []} />
          </div>
        ) : null}
      </section>
    </AdminShell>
  );
}
